<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: Renders the mapping table behind Logic.mappingTable — header data, entries and overrides widgets, typed dependants with their coverage limit, and the shared quick test.
-->

@if (initialized) {

    <div class="flex flex-column gap-4">

        @if (isUnconfigured()) {

            <div class="surface-ground border-round-lg p-4 flex flex-column align-items-center gap-2">
                <i class="fa-regular fa-arrow-right-arrow-left text-4xl text-gray-600"></i>
                <span class="text-lg font-semibold">Keine Mapping-Tabelle zugeordnet</span>
                <span class="text-sm text-gray-600 text-center">
                    Diese Mapping-Logik zeigt noch auf keine Wertetabelle und kann deshalb keinen Wert
                    auflösen. Wählen Sie die Tabelle im Bearbeitungsbereich der Logik aus.
                </span>
            </div>

        } @else {

            <!-- Header data of the table -->
            @if (busy) {
                <p-skeleton height="8rem"></p-skeleton>
            } @else if (loadErrorMessage) {
                <p-message severity="error" [text]="loadErrorMessage"></p-message>
            } @else if (mappingTable) {
                <div class="surface-card border-1 surface-border border-round-lg p-3">
                    <div class="flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
                        <div class="flex flex-column">
                            <span class="text-lg font-semibold">{{ mappingTable.name }}</span>
                            <span class="text-sm text-gray-600">{{ mappingTable.alias }}</span>
                        </div>
                        <p-button label="Tabelle öffnen"
                                  icon="fa-regular fa-arrow-up-right-from-square"
                                  [outlined]="true"
                                  severity="secondary"
                                  (onClick)="openMappingTable($event)"></p-button>
                    </div>

                    <div class="grid">
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Logikgruppe</div>
                            <div class="text-base">{{ mappingTable.logicGroupDtoName || '—' }}</div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Fallback-Verhalten</div>
                            <div class="text-base">{{ getFallbackLabel() }}</div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600 flex align-items-center gap-1">
                                Quell-Typ
                                <i class="fa-regular fa-circle-info"
                                   pTooltip="Reine Beschriftung der Spalten — beschreibt, was Quell- und Zielwerte bedeuten, und hat keine Wirkung auf die Auflösung zur Laufzeit."
                                   tooltipPosition="top"></i>
                            </div>
                            <div class="text-base">{{ mappingTable.sourceType || '—' }}</div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600 flex align-items-center gap-1">
                                Ziel-Typ
                                <i class="fa-regular fa-circle-info"
                                   pTooltip="Reine Beschriftung der Spalten — beschreibt, was Quell- und Zielwerte bedeuten, und hat keine Wirkung auf die Auflösung zur Laufzeit."
                                   tooltipPosition="top"></i>
                            </div>
                            <div class="text-base">{{ mappingTable.targetType || '—' }}</div>
                        </div>
                        @if (showsFallbackValue()) {
                            <div class="col-12 md:col-6 mb-2">
                                <div class="text-sm text-gray-600">Fallback-Wert</div>
                                <div class="text-base">{{ mappingTable.fallbackValue || '(leerer Text)' }}</div>
                            </div>
                        }
                        @if (mappingTable.fallbackLogicDtoName) {
                            <div class="col-12 md:col-6 mb-2">
                                <div class="text-sm text-gray-600">Fallback-Logik</div>
                                <div class="text-base">{{ mappingTable.fallbackLogicDtoName }}</div>
                            </div>
                        }
                    </div>
                </div>
            }

            <!-- Entries -->
            @if (widgetEntries) {
                <div class="surface-section border-round-lg shadow-1">
                    <mvs-widget [widgetData]="widgetEntries"></mvs-widget>
                </div>
            }

            <!-- Partner overrides -->
            @if (widgetOverrides) {
                <div class="surface-section border-round-lg shadow-1">
                    <mvs-widget [widgetData]="widgetOverrides"></mvs-widget>
                </div>
            }

            <!-- Typed dependants + the coverage limit that must travel with them -->
            <div class="surface-card border-1 surface-border border-round-lg p-3">
                <div class="flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
                    <span class="text-base font-semibold">Verwendung über Fremdschlüssel</span>
                    @if (!dependentsBusy && !dependentsErrorMessage) {
                        <p-tag [value]="getDependentsCountText()"
                               severity="secondary"
                               [rounded]="true"></p-tag>
                    }
                </div>

                @if (dependentsBusy) {
                    <p-skeleton height="4rem"></p-skeleton>
                } @else if (dependentsErrorMessage) {
                    <p-message severity="error" [text]="dependentsErrorMessage"></p-message>
                } @else if (!dependents.length) {
                    <div class="text-sm text-gray-600 mb-3">
                        Keine Referenz über einen Fremdschlüssel gefunden.
                    </div>
                } @else {
                    <div class="flex flex-column gap-2 mb-3">
                        @for (dependent of dependents; track dependent.entityId) {
                            <div class="flex align-items-center gap-3 flex-wrap">
                                <p-tag [value]="getUsageTypeLabel(dependent)"
                                       severity="secondary"
                                       [rounded]="true"></p-tag>
                                <span class="text-base">{{ dependent.name || dependent.alias || ('#' + dependent.entityId) }}</span>
                                @if (dependent.alias && dependent.name) {
                                    <span class="text-sm text-gray-600">{{ dependent.alias }}</span>
                                }
                            </div>
                        }
                    </div>
                }

                <!--
                  Mandatory next to the list, empty or not: the query walks typed foreign keys only.
                  Without this note an empty list reads as 'nothing depends on this table', which it
                  does not mean.
                -->
                <div class="bg-yellow-50 border-round-md p-3">
                    <div class="flex align-items-start gap-2">
                        <i class="fa-solid fa-triangle-exclamation text-yellow-600 mt-1"></i>
                        <span class="text-sm text-yellow-600">{{ dependencyCoverageNote }}</span>
                    </div>
                </div>
            </div>

            <!-- Shared quick test -->
            <div class="surface-card border-1 surface-border border-round-lg p-3">
                <mvs-lg-mapping-quick-test
                        [mappingTableId]="getMappingTableId()"
                        [mappingTableDto]="mappingTable"></mvs-lg-mapping-quick-test>
            </div>

        }
    </div>
}
